// Componentes auxiliares del flow multi-select de plantillas:
//
//   • MultiPresetCarousel — reemplaza la imagen sola del PresetDetailSheet
//     cuando el user vino del multi-select. Carrusel horizontal con snap,
//     badge "Múltiples seleccionadas" arriba y dots de paginación abajo.
//   • MultiSelectedStrip  — carrusel horizontal compacto que muestra las
//     plantillas seleccionadas en el step 'preview' con multi-select
//     activo, con X para quitar cada una y "Cancelar" para salir del
//     modo.

import React, { useRef, useState } from 'react';
import { Copy, X } from 'lucide-react';
import type { PresetV2 } from '../mockData';

export interface MultiPresetCarouselProps {
  presets: PresetV2[];
  /** Cómo ajustar las imágenes al container: cover llena (recortando),
   *  contain mantiene aspect (con bandas si no coincide). El layout
   *  desktop usa cover (cards 3:4) y mobile también cover. */
  fit?: 'cover' | 'contain';
}

export const MultiPresetCarousel: React.FC<MultiPresetCarouselProps> = ({
  presets,
  fit = 'cover',
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActiveIdx(Math.max(0, Math.min(presets.length - 1, idx)));
  };
  return (
    <div className="absolute inset-0">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full w-full overflow-x-auto snap-x snap-mandatory flex scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {presets.map((p) => (
          <div
            key={p.id}
            className="snap-center shrink-0 w-full h-full relative bg-neutral-100"
          >
            {p.outputType === 'video' ? (
              <video
                src={p.imageUrlFull || p.imageUrl}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                poster={p.imageUrl}
                className={`absolute inset-0 w-full h-full ${
                  fit === 'cover' ? 'object-cover' : 'object-contain'
                }`}
              />
            ) : (
              <img
                src={p.imageUrlFull || p.imageUrl}
                alt={p.title}
                className={`absolute inset-0 w-full h-full ${
                  fit === 'cover' ? 'object-cover' : 'object-contain'
                }`}
              />
            )}
          </div>
        ))}
      </div>
      {/* Badge "Múltiples seleccionadas" — blanco semi-transparente. En
          mobile va centrado horizontal; en desktop ancla arriba-izq. */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 md:left-4 md:translate-x-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/40 backdrop-blur-md text-white text-[12.5px] font-semibold shadow-sm whitespace-nowrap">
        <Copy className="w-3.5 h-3.5" />
        Múltiples seleccionadas
      </div>
      {/* Dots — mismo color, abajo centrados. El activo más opaco/ancho. */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 inline-flex items-center gap-1.5">
        {presets.map((p, i) => (
          <span
            key={p.id}
            className={`h-1.5 rounded-full transition-all ${
              i === activeIdx
                ? 'w-5 bg-white/85'
                : 'w-1.5 bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export interface MultiSelectedStripProps {
  selected: PresetV2[];
  onRemove: (id: string) => void;
  onCancel: () => void;
  /** Si true, omite el botón Cancelar (mobile ya lo tiene en el title row). */
  hideCancel?: boolean;
}

export const MultiSelectedStrip: React.FC<MultiSelectedStripProps> = ({
  selected,
  onRemove,
  onCancel,
  hideCancel = false,
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg ring-1 ring-black/[0.05] px-3 py-2.5 flex items-center gap-2.5">
      <div
        className="flex items-center gap-2 overflow-x-auto flex-1 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {selected.length === 0 ? (
          <p className="text-[13px] text-[#3C3C43]/55 leading-snug px-1">
            Tocá las plantillas para sumarlas a la selección.
          </p>
        ) : (
          selected.map((p) => (
            <div
              key={p.id}
              // animate-drop-bounce: cuando un thumbnail se monta
              // (porque el user acaba de añadir esa plantilla al
              // strip), cae desde arriba con un pequeño rebote. Los
              // existentes solo animan en su primer mount; remontaje
              // por key requiere desselect+select.
              className="shrink-0 relative w-14 h-14 rounded-lg overflow-hidden bg-neutral-100 ring-1 ring-black/[0.05] animate-drop-bounce"
            >
              <img
                src={p.imageUrl}
                alt={p.title}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => onRemove(p.id)}
                aria-label={`Quitar ${p.title}`}
                className="absolute top-0.5 right-0.5 w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-black/65 text-white hover:bg-black/80 ring-1 ring-white/40 shadow-sm transition-colors"
              >
                <X className="w-2.5 h-2.5" strokeWidth={2.75} />
              </button>
            </div>
          ))
        )}
      </div>
      {!hideCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 text-[13px] text-brand-primary hover:opacity-75 transition-opacity font-medium"
        >
          Cancelar
        </button>
      )}
    </div>
  );
};
